import { Grid, Skeleton, Stack, Typography } from "@mui/material";

import { useMountEffect } from "@/modules/common/hooks/useMountEffect";

type Props = {
  children?: React.ReactNode;
  actions?: React.ReactNode;
  title?: string;
  icon?: React.ElementType;
  disablePadding?: boolean;
  isLoading?: boolean;
  pageId?: string;
  height?: number | string;
  marginBottom?: number | string;
};

const PageTitle = ({ title }: { title: string }): null => {
  useMountEffect(() => {
    document.title = title;
  });

  return null;
};

const TitledPage = ({
  children,
  actions,
  title,
  icon: Icon,
  disablePadding,
  isLoading,
  pageId,
  height,
  marginBottom,
}: Props): JSX.Element => {
  return (
    <Grid
      container
      id={pageId}
      sx={{
        height: height ?? "100%",
        alignContent: "start",
        p: disablePadding ? 0 : 3,
        mb: marginBottom ?? (disablePadding ? 0 : 3),
        gap: 3,
        bgcolor: (theme) => theme.palette.background.default,
      }}
    >
      {title && <PageTitle key={title} title={title} />}
      <Grid size={12}>
        <Stack
          sx={{
            justifyContent: "space-between",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 2 },
          }}
        >
          <Stack sx={{ flexDirection: "row", alignItems: "center", gap: 1 }}>
            {isLoading ? (
              <Skeleton variant="circular" width={30} height={30} />
            ) : (
              Icon && <Icon style={{ fontSize: 30 }} />
            )}
            <Typography
              variant="h5"
              component="h2"
              sx={{
                overflowWrap: "anywhere",
              }}
            >
              {isLoading ? <Skeleton width={200} /> : title}
            </Typography>
          </Stack>
          <Stack
            sx={{
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 2, sm: 4 },
              alignItems: { xs: "initial", sm: "center" },
              flex: 1,
              justifyContent: { xs: "initial", sm: "flex-end" },
            }}
          >
            {isLoading ? (
              <Skeleton
                variant="rectangular"
                height={40}
                sx={{
                  width: { xs: "100%", sm: 160 },
                }}
              />
            ) : (
              actions
            )}
          </Stack>
        </Stack>
      </Grid>
      <Grid size={12}>
        {isLoading ? (
          <Skeleton
            variant="rectangular"
            sx={{
              height: {
                xs: "calc(100vh - 225px)",
                sm: "calc(100vh - 178px)",
              },
            }}
          />
        ) : (
          children
        )}
      </Grid>
    </Grid>
  );
};

export default TitledPage;
